import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import { researchSources } from '../../data/researchDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { formatId, formatTimestamp } from '../../utils/demoStorage';
import { errorClass, fieldClass, glassPanel, labelClass, linkButton, primaryButton } from '../../utils/demoUi';
import { sourceById } from '../../utils/researchDemo';
const notePrompt = 'What does the evidence suggest? What remains uncertain?';
export function ResearchNotebook() {
  const {
    state,
    dispatch,
    inspectSource
  } = useResearchDemo();
  const draft = state.ui.noteDraft;
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!sourceById(draft.sourceId)) return setError('Choose the source this note relates to.');
    if (!draft.text.trim()) return setError('Write a note before saving.');
    const nextId = formatId('NOTE', state.counters.note + 1);
    dispatch({
      type: 'saveNote'
    });
    setError('');
    setSaved(`${nextId} saved and linked to ${draft.sourceId}.`);
  };
  return <div data-id="element-2246" data-mp="2ca01s5kvs3r0">
      <DemoSectionHeader title="Research Notebook" description="Notes stay linked to the source they discuss. Unsaved drafts are kept while you move between sections and workspaces." data-id="element-2247" data-mp="2c9qdcmfhi4nf" />

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" data-id="element-2248" data-mp="1jmw12cstmbl1">
        <form onSubmit={save} className={`${glassPanel} h-fit p-6`} noValidate data-id="element-2249" data-mp="1fmd98iqxh5iu">
          <label htmlFor="note-source" className={labelClass} data-id="element-2250" data-mp="1tllariakcpq7">
            Linked source
          </label>
          <select id="note-source" value={draft.sourceId} onChange={event => dispatch({
          type: 'setNoteDraft',
          draft: {
            sourceId: event.target.value
          }
        })} className={fieldClass} data-id="element-2251" data-mp="1okp8ta9rvdnf">
            {researchSources.map(source => <option key={source.id} value={source.id} data-id="element-2252" data-mp="380a44vtj3ov4">
                {source.id} — {source.title}
              </option>)}
          </select>

          <label htmlFor="note-text" className={`mt-5 ${labelClass}`} data-id="element-2253" data-mp="3c4rkel0ojk15">
            {notePrompt}
          </label>
          <textarea id="note-text" rows={7} value={draft.text} onChange={event => {
          setSaved('');
          dispatch({
            type: 'setNoteDraft',
            draft: {
              text: event.target.value
            }
          });
        }} aria-invalid={Boolean(error)} aria-describedby={error ? 'note-error' : undefined} className={fieldClass} data-id="element-2254" data-mp="3m0um5prmdgnk" />
          {error && <p id="note-error" className={errorClass} data-id="element-2255" data-mp="3330h1mtm3pag">
              {error}
            </p>}
          <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-2256" data-mp="25m1ou4fpgduu">
            <button type="submit" className={primaryButton} data-id="element-2257" data-mp="2aleh54pcid4t">
              Save note
            </button>
            <p role="status" className="text-sm font-medium text-navy" data-id="element-2258" data-mp="232opar2kv0t9">
              {saved}
            </p>
          </div>
        </form>

        <section aria-labelledby="saved-notes" data-id="element-2259" data-mp="1y43jmc4k3oxf">
          <h2 id="saved-notes" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2260" data-mp="1t3pm3o0kzxdn">
            Saved notes ({state.notes.length})
          </h2>
          {state.notes.length === 0 ? <p className="mt-3 text-sm text-ink-muted" data-id="element-2261" data-mp="1k42lh4foevln">No notes yet. Saved notes appear here with their source.</p> : <ul className="mt-3 space-y-3" data-id="element-2262" data-mp="1fc2fovcxd8h5">
              {state.notes.map(note => <li key={note.id} className={`${glassPanel} p-5`} data-id="element-2263" data-mp="1aestrpoqvey2">
                  <p className="flex flex-wrap gap-x-3 text-xs text-ink-muted" data-id="element-2264" data-mp="15ewntkwhldfv">
                    <span className="font-mono font-semibold" data-id="element-2265" data-mp="15ctq0ep3eg0l">{note.id}</span>
                    <span className="font-mono font-semibold" data-id="element-2266" data-mp="10es5rqfxkanj">{note.sourceId}</span>
                    <span data-id="element-2267" data-mp="wf9or5r8cjpz">{formatTimestamp(note.createdAt)}</span>
                  </p>
                  <p className="mt-2 whitespace-pre-wrap leading-relaxed text-ink-dark" data-id="element-2268" data-mp="1l5nogn8cod3g">{note.text}</p>
                  <button type="button" onClick={() => inspectSource(note.sourceId)} className={`mt-3 ${linkButton}`} data-id="element-2269" data-mp="1q2e2l50lgym8">
                    Inspect {note.sourceId}
                    <ArrowRight size={14} aria-hidden="true" data-id="element-2270" data-mp="1gdnvjeqp6kgd" />
                  </button>
                </li>)}
            </ul>}
        </section>
      </div>
    </div>;
}